import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    Divider,
    Grid,
    makeStyles,
    Switch,
    Tab,
    Tabs,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import useRouter from 'use-react-router';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {GroupBooking, RoomPartyMember} from '../../../graphql/groups.datatypes';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import createGroupBooking, {CreateGroupBookingInput} from './createGroupBooking.api';
import deleteGroupBooking from './deleteGroupBooking.api';
import updateGroupBooking, {UpdateGroupBookingInput} from './updateGroupBooking.api';
import {BillingPartyListContent} from '../../BillingParty/BillingPartyList/BillingPartyList';
import {RoomPartyListContent} from '../../RoomParty/RoomPartyList/RoomPartyList';
import MergeRoomPartyModal from '../../RoomParty/MergeRoomPartyModal';
import {RoomPartyMemberOptionsEdit} from '../../RoomParty/RoomPartyMemberOptionsEdit';
import {WCVDatePicker} from '../../../components/DateUtilsProvider/WCVDatePicker';
import {formatGraphQlDate} from '../../../utils/formatters';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(3),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  card: {},
  saveButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: theme.palette.error.light,
    '&:hover': {
      backgroundColor: theme.palette.error.main
    }
  },
  cloneButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[400],
    '&:hover': {
      backgroundColor: colors.green[700]
    }
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.error.main,
  },
  marginBottom: {
    paddingLeft: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  tabs: {
    marginTop: theme.spacing(3)
  },
}));


export const GroupBookingEdit = (props: { groupBooking: GroupBooking }) => {
  const classes = useStyles();
  const {groupBooking} = props;
  const isNewBooking = groupBooking.id === -1;
  const {history} = useRouter();
  const [open, setOpen] = React.useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const [sendCreateGroupBooking, createData] = createGroupBooking();
  const [sendUpdateGroupBooking, updateData] = updateGroupBooking();
  const [sendDeleteGroupBooking, deleteData] = deleteGroupBooking();
  const [tab, setTab] = useState('details');
  const roomPartyMembers = groupBooking.roomParties ? groupBooking.roomParties.reduce((members: RoomPartyMember[], roomParty) => roomParty.members ? [...members, ...roomParty.members] : members, []) : [];

  //1. Set form and error state
  const [formState, setFormState] = useState({...groupBooking});
  const loadingData = anyLoading(createData, updateData, deleteData);
  const [errors, setErrors] = useState({
    isValid: false,
    errors: {
      name: false,
      description: false,
      start_date: false,
      end_date: false,
      admissions_cutoff: false,
    }
  });

  useEffect(() => {
    setFormState({...groupBooking});
  }, [groupBooking]);

  //2. Setup helper functions for form
  const handleFieldChange = getHandleFieldChange(setFormState);
  const hasError = getHasError(errors);

  //3. setup validation and error hook
  const validateSchema: any = {
  };

  useEffect(() => {
    const newErrors = validate(formState, validateSchema);

    setErrors(state => ({
      ...state,
      isValid: !newErrors,
      errors: newErrors || {}
    }));
  }, [formState]);

  //4. Handle save form
  const onSaveClick = () => {
    if (isNewBooking) {
      const sanitisedData: CreateGroupBookingInput = {
        data: {
          ...{
              ...formState,
              id: undefined,
              __typename: undefined,
              start_date: formatGraphQlDate(formState.start_date)!,//moment(formState.start_date).format('YYYY-MM-DD'),
              end_date: formatGraphQlDate(formState.end_date)!,//moment(formState.end_date).format('YYYY-MM-DD'),
              group: undefined,
              created_at: undefined,
              billingParties: undefined,
              roomParties: undefined,
              cost: undefined,
              outstandingBalance: undefined,
              overduePayments: undefined,
          },
        }
      };

      sendCreateGroupBooking({variables: sanitisedData})
          .then((response) => {
            enqueueSnackbar('Group Booking Saved.', {variant: 'success'});
            history.push('/group_bookings/' + response.data!.createGroupBooking.id);
          })
        .catch(() => {
          enqueueSnackbar('Create Group Booking Failed!', {variant: 'error'});
        });
    } else {
      const sanitisedData: UpdateGroupBookingInput = {
        data: {
          ...{
              ...formState,
              __typename: undefined,
              start_date: formatGraphQlDate(formState.start_date)!,//moment(formState.start_date).format('YYYY-MM-DD'),
              end_date: formatGraphQlDate(formState.end_date)!,//moment(formState.end_date).format('YYYY-MM-DD'),
              group: undefined,
              created_at: undefined,
              billingParties: undefined,
              roomParties: undefined,
              cost: undefined,
              outstandingBalance: undefined,
              overduePayments: undefined,
          },
        }
      };

      sendUpdateGroupBooking({variables: sanitisedData})
        .then(() => {
          enqueueSnackbar('Group Booking Updated.', {variant: 'success'});
        })
        .catch(() => {
          enqueueSnackbar('Group Booking Update Failed!', {variant: 'error'});
        });
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    sendDeleteGroupBooking({variables: {id: formState.id}})
        .then(() => {
          enqueueSnackbar('Group Booking Deleted.', {variant: 'success'});
          history.push('/groups');
        })
        .catch(() => {
          enqueueSnackbar('Deleted Group Booking Failed!', {variant: 'error'});
        });

    handleClose();
  };

  //Merge modal functions
  const [openMergePopup, setOpenMergePopup] = useState(false);
  const handleOpenMergePopup = () => {
    setOpenMergePopup(true);
  };
  const handleCloseMergePopup = () => {
    setOpenMergePopup(false);
  };

  if (!groupBooking.group) return <div>loading...</div>

  return (
      <form>
        <LoadingOverlay loading={loadingData.loading}>

          {!isNewBooking && (
              <Card className={classes.container}>
                <CardHeader
                    title={'Booking Totals'}
                    action={<Button variant="outlined"
                                    onClick={() => history.push('/admin_booking/' + groupBooking.group!.booking_form_url + '/' + groupBooking.id)}>Load
                      In Booking Tool</Button>}
                />
                <Divider/>
                <CardContent className={classes.card}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item lg={3} md={6} xs={12}>
                      <Typography variant="h3">Grand Total: ${groupBooking.cost}</Typography>
                    </Grid>
                    <Grid item lg={3} md={6} xs={12}>
                      <Typography variant="h3">Outstanding: ${groupBooking.outstandingBalance}</Typography>
                    </Grid>
                    {groupBooking.overduePayments && (
                        <Grid item lg={3} md={6} xs={12}>
                          <Typography variant="h3">Past due: ${groupBooking.overduePayments}</Typography>
                        </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
          )}

          <Tabs className={classes.tabs} onChange={(event, value) => setTab(value)} scrollButtons="auto" value={tab}
                variant="scrollable">
            <Tab label="Details" value="details"/>
            {!isNewBooking && <Tab label="Billing Parties" value="billings"/>}
            {!isNewBooking && <Tab label="Room Parties" value="rooms"/>}
            {!isNewBooking && <Tab label="Member Options" value="options"/>}
          </Tabs>

          {tab === 'details' && (
              <Card className={classes.container}>
                <CardHeader title="Edit Group Booking"/>
                <CardContent className={classes.card}>

                  <Grid container spacing={3}>

                    <Grid item xs={12}>
                      <TextField
                          fullWidth
                          label="Group"
                          name="group"
                          value={groupBooking.group!.name}
                          variant="outlined"
                          disabled={true}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={3}>
                          <Grid item md={6}>
                              <WCVDatePicker
                                  {...hasError('start_date')}
                                  id="start_date"
                                  label="Start Date:"
                                  value={formState.start_date}
                                  onChange={(date, value) => {
                                      setFormState({
                                          ...formState,
                                          start_date: value ? value : undefined
                                      })
                                  }}
                              />
                          </Grid>
                          <Grid item md={6}>
                              <WCVDatePicker
                                  {...hasError('end_date')}
                                  id="end_date"
                                  label="End Date:"
                                  value={formState.end_date}
                                  onChange={(date, value) => {
                                      setFormState({
                                          ...formState,
                                          end_date: value ? value : undefined
                                      })
                                  }}
                              />
                          </Grid>
                      </Grid>
                    </Grid>

                      <Grid item xs={12}>
                          <Grid component="label" container alignItems="center" spacing={1}>
                              <Grid item>
                                  <Switch
                                      checked={formState.soft_cancelled}
                                      color="secondary"
                                      edge="start"
                                      name="soft_cancelled"
                                      onChange={handleFieldChange}
                                      value={formState.soft_cancelled}
                                  />
                              </Grid>
                              <Grid item><Typography variant="h5">Soft Cancelled?</Typography></Grid>
                          </Grid>
                      </Grid>

                      <Grid item xs={12}>
                          <Grid component="label" container alignItems="center" spacing={1}>
                              <Grid item>
                                  <Switch
                                      checked={formState.admin_locked}
                                      color="secondary"
                                      edge="start"
                                      name="admin_locked"
                                      onChange={handleFieldChange}
                                      value={formState.admin_locked}
                                      disabled={true}
                                  />
                              </Grid>
                              <Grid item><Typography variant="h5">Admin Locked?</Typography></Grid>
                          </Grid>
                      </Grid>

                      <Grid item xs={12}>
                          <Grid component="label" container alignItems="center" spacing={1}>
                              <Grid item>
                                  <Switch
                                      checked={formState.disabled}
                                      color="secondary"
                                      edge="start"
                                      name="disabled"
                                      onChange={handleFieldChange}
                                      value={formState.disabled}
                                  />
                              </Grid>
                              <Grid item><Typography variant="h5">Disabled?</Typography></Grid>
                          </Grid>
                      </Grid>

                      <Grid item xs={12}>
                          <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                  disabled={!errors.isValid}>Save</Button>

                          <Can permission="groups.delete">
                              <Button onClick={handleClickOpen} className={classes.deleteButton} variant="contained"
                                      disabled={isNewBooking}>
                                  Delete
                              </Button>
                          </Can>
                      </Grid>

                  </Grid>
                </CardContent>
              </Card>
          )}

          {tab === 'billings' && (
              <Card className={classes.container}>
                <CardHeader title="Billing Parties" action={
                  <Button color="primary" variant="outlined"
                          onClick={() => history.push('/billing_party/new/' + groupBooking.id)}>Add Billing
                    Party</Button>
                }/>
                <CardContent className={classes.card}>
                  <BillingPartyListContent bookingId={groupBooking.id} isAdmin={true} canEdit={true}/>
                </CardContent>
              </Card>
          )}

          {tab === 'rooms' && (
              <Card className={classes.container}>
                <CardHeader title="Room Parties" action={[
                  <Button key="add" color="primary" variant="outlined"
                          onClick={() => history.push('/room_party/new/' + groupBooking.id)}>Add Room Party</Button>,

                  <Button key="merge" color="primary" variant="outlined"
                          onClick={handleOpenMergePopup}>Merge Room Parties</Button>,

                ]}/>
                <CardContent className={classes.card}>
                  <RoomPartyListContent bookingId={groupBooking.id} isAdmin={true} canEdit={true}/>
                </CardContent>
              </Card>
          )}


          {tab === 'options' && (
              <RoomPartyMemberOptionsEdit groupId={groupBooking.group_id} roomPartyMembers={roomPartyMembers} isAdmin={true} canEdit={true}/>
          )}

        </LoadingOverlay>

        <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
        {openMergePopup && (<MergeRoomPartyModal onClose={handleCloseMergePopup} open={openMergePopup}
                                                 groupId={groupBooking.group_id}/>)}
      </form>
  );
};

export default GroupBookingEdit;
